import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtCVE, fmtDate, PAYMENT_METHODS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, PencilSimple, Trash, CircleNotch, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { date: today(), category: "Outros", description: "", amount: "", method: "numerario", supplier: "" };

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/expenses", { params: { category: filter || undefined } });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/expenses/categories").then((r) => setCats(r.data.items)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setF({ ...EMPTY, date: today() }); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setF({ ...EMPTY, ...e }); setOpen(true); };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.description.trim() || !(Number(f.amount) > 0)) return toast.error("Indique descrição e valor");
    setBusy(true);
    try {
      const payload = { ...f, amount: Number(f.amount) };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editing ? "Despesa atualizada" : "Despesa registada");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Eliminar "${e.description}"?`)) return;
    try { await api.delete(`/expenses/${e.id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="expenses-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Tesouraria</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Despesas</h1>
        </div>
        <div className="flex gap-2">
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="expenses-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas as categorias</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button className="rounded-full" onClick={openNew} data-testid="new-expense-btn"><Plus size={18} className="mr-1.5" /> Nova despesa</Button>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-4 text-sm">Total: <b className="font-display text-lg" data-testid="expenses-total">{fmtCVE(total)}</b></div>
      <div className="rounded-xl border border-border bg-card">
        {loading ? <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
          : items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center"><Wallet size={40} className="text-muted-foreground" /><p className="text-muted-foreground">Sem despesas registadas.</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Data</TableHead><TableHead>Descrição</TableHead><TableHead className="hidden md:table-cell">Categoria</TableHead>
                <TableHead className="hidden lg:table-cell">Fornecedor</TableHead><TableHead className="text-right">Valor</TableHead><TableHead className="text-right">Ações</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} className="stagger-item" data-testid={`expense-row-${e.id}`}>
                    <TableCell className="text-muted-foreground">{fmtDate(e.date)}</TableCell>
                    <TableCell className="font-medium">{e.description}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{e.category}</TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground">{e.supplier || "—"}</TableCell>
                    <TableCell className="text-right font-medium">{fmtCVE(e.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(e)} data-testid={`delete-expense-${e.id}`}><Trash size={16} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar despesa" : "Nova despesa"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>Descrição *</Label><Input value={f.description} onChange={set("description")} data-testid="expense-description" /></div>
            <div className="space-y-1.5"><Label>Valor (CVE) *</Label><Input type="number" min="0" value={f.amount} onChange={set("amount")} data-testid="expense-amount" /></div>
            <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={f.date} onChange={set("date")} data-testid="expense-date" /></div>
            <div className="space-y-1.5"><Label>Categoria</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Método</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="expense-method"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PAYMENT_METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-2 space-y-1.5"><Label>Fornecedor</Label><Input value={f.supplier} onChange={set("supplier")} data-testid="expense-supplier" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-expense-btn">{busy ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
